import React, { useState, useEffect } from 'react'
import {
  Box,
  VStack,
  HStack,
  Text,
  Input,
  Button,
  FormControl,
  FormLabel,
  Alert,
  AlertIcon,
  InputGroup,
  InputRightElement,
  IconButton,
  Progress,
  Icon,
  useColorModeValue
} from '@chakra-ui/react'
import { FaEye, FaEyeSlash, FaCheck, FaTimes } from 'react-icons/fa'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { httpsCallable } from 'firebase/functions'
import { functions } from '@/config/firebase'

// Common passwords to check against
const COMMON_PASSWORDS = [
  'password', '123456', '123456789', 'qwerty', 'abc123', 'password123',
  'admin', 'letmein', 'welcome', 'monkey', 'dragon', 'master', 'sunshine',
  'princess', 'qwerty123', 'admin123', 'password1', '12345678', 'baseball',
  'football', 'superman', 'trustno1', 'butterfly', 'dragon123', 'master123'
]

interface PasswordRequirement {
  label: string
  test: (password: string) => boolean
  met: boolean
}

export const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token')

  const [isValidating, setIsValidating] = useState(true)
  const [isValid, setIsValid] = useState(false)
  const [email, setEmail] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const cardBg = useColorModeValue('#191919', '#191919')
  const borderColor = useColorModeValue('gray.700', 'gray.700')
  const textColor = useColorModeValue('white', 'white')
  const labelColor = useColorModeValue('gray.300', 'gray.300')
  const inputBg = useColorModeValue('#191919', '#191919')
  const inputBorder = useColorModeValue('gray.600', 'gray.600')

  // OWASP Password Requirements
  const passwordRequirements: PasswordRequirement[] = [
    {
      label: 'At least 8 characters long',
      test: (password: string) => password.length >= 8,
      met: false
    },
    {
      label: 'Contains at least one uppercase letter (A-Z)',
      test: (password: string) => /[A-Z]/.test(password),
      met: false
    },
    {
      label: 'Contains at least one lowercase letter (a-z)',
      test: (password: string) => /[a-z]/.test(password),
      met: false
    },
    {
      label: 'Contains at least one number (0-9)',
      test: (password: string) => /\d/.test(password),
      met: false
    },
    {
      label: 'Contains at least one special character (!@#$%^&*)',
      test: (password: string) => /[!@#$%^&*(),.?":{}|<>]/.test(password),
      met: false
    },
    {
      label: 'Not a common password',
      test: (password: string) => !COMMON_PASSWORDS.includes(password.toLowerCase()),
      met: false
    },
    {
      label: 'No sequential characters (123, abc)',
      test: (password: string) => {
        const lower = password.toLowerCase()
        for (let i = 0; i < lower.length - 2; i++) {
          const seq1 = lower.charCodeAt(i + 1) - lower.charCodeAt(i)
          const seq2 = lower.charCodeAt(i + 2) - lower.charCodeAt(i + 1)
          if (seq1 === 1 && seq2 === 1) return false
        }
        return true
      },
      met: false
    },
    {
      label: 'No repeated characters (aaa, 111)',
      test: (password: string) => {
        for (let i = 0; i < password.length - 2; i++) {
          if (password[i] === password[i + 1] && password[i] === password[i + 2]) {
            return false
          }
        }
        return true
      },
      met: false
    }
  ]

  // Calculate password strength
  const calculatePasswordStrength = (password: string): number => {
    if (!password) return 0
    
    let strength = 0
    passwordRequirements.forEach(req => {
      if (req.test(password)) strength += 12.5 // 100% / 8 requirements
    })
    
    return Math.min(100, strength)
  }

  // Get password strength color
  const getPasswordStrengthColor = (strength: number): string => {
    if (strength < 25) return 'red.400'
    if (strength < 50) return 'orange.400'
    if (strength < 75) return 'yellow.400'
    return 'green.400'
  }

  // Get password strength label
  const getPasswordStrengthLabel = (strength: number): string => {
    if (strength < 25) return 'Very Weak'
    if (strength < 50) return 'Weak'
    if (strength < 75) return 'Moderate'
    if (strength < 100) return 'Strong'
    return 'Very Strong'
  }

  // Update password requirements based on current password
  const currentPasswordRequirements = passwordRequirements.map(req => ({
    ...req,
    met: req.test(newPassword)
  }))

  const passwordStrength = calculatePasswordStrength(newPassword)
  const strengthColor = getPasswordStrengthColor(passwordStrength)
  const strengthLabel = getPasswordStrengthLabel(passwordStrength)

  // Check if password meets minimum requirements (75% strength)
  const isPasswordValid = passwordStrength >= 75

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError('Invalid reset link. Please request a new password reset.')
        setIsValidating(false)
        return
      }

      try {
        const verifyPasswordResetToken = httpsCallable(functions, 'verifyPasswordResetToken')
        const result = await verifyPasswordResetToken({ token })
        const data = result.data as { valid: boolean; email: string }
        
        if (data.valid) {
          setIsValid(true)
          setEmail(data.email)
        }
      } catch (err: any) {
        console.error('Token verification error:', err)
        setError(err.message || 'Invalid or expired reset link. Please request a new password reset.')
      } finally {
        setIsValidating(false)
      }
    }

    verifyToken()
  }, [token])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // Validate password strength
    if (!isPasswordValid) {
      setError('Password does not meet the security requirements. Please choose a stronger password.')
      return
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    setIsLoading(true)

    try {
      const resetPasswordWithToken = httpsCallable(functions, 'resetPasswordWithToken')
      await resetPasswordWithToken({ token, newPassword })
      setSuccess(true)
    } catch (err: any) {
      console.error('Password reset error:', err)
      setError(err.message || 'Failed to reset password. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  // Loading state while validating token
  if (isValidating) {
    return (
      <Box minH="100vh" bg="#09090b" display="flex" alignItems="center" justifyContent="center" p={4}>
        <Box maxW="md" w="full" bg={cardBg} rounded="lg" shadow="md" p={6} border="1px" borderColor={borderColor}>
          <VStack spacing={4}>
            <Text color={textColor} fontSize="lg">Verifying reset link...</Text>
          </VStack>
        </Box>
      </Box>
    )
  }

  // Success state
  if (success) {
    return (
      <Box minH="100vh" bg="#09090b" display="flex" alignItems="center" justifyContent="center" p={4}>
        <Box maxW="md" w="full" bg={cardBg} rounded="lg" shadow="md" p={6} border="1px" borderColor={borderColor}>
          <VStack spacing={4} textAlign="center">
            <Box color="green.400">
              <FaCheck size={48} />
            </Box>
            <Text fontSize="2xl" fontWeight="bold" color={textColor}>
              Password Reset Successful
            </Text>
            <Text color="gray.400">
              Your password has been updated. You can now log in with your new password.
            </Text>
            <Button
              w="full"
              bg="#3182ce"
              color="white"
              _hover={{ bg: '#2c5aa0' }}
              onClick={() => navigate('/auth')}
            >
              Go to Login
            </Button>
          </VStack>
        </Box>
      </Box>
    )
  }

  // Invalid token state
  if (!isValid) {
    return (
      <Box minH="100vh" bg="#09090b" display="flex" alignItems="center" justifyContent="center" p={4}>
        <Box maxW="md" w="full" bg={cardBg} rounded="lg" shadow="md" p={6} border="1px" borderColor={borderColor}>
          <VStack spacing={4} textAlign="center">
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              {error || 'Invalid reset link'}
            </Alert>
            <Text color="gray.400">
              The password reset link is invalid or has expired.
            </Text>
            <Button
              w="full"
              bg="#3182ce"
              color="white"
              _hover={{ bg: '#2c5aa0' }}
              onClick={() => navigate('/auth')}
            >
              Request New Reset Link
            </Button>
          </VStack>
        </Box>
      </Box>
    )
  }

  // Reset password form
  return (
    <Box minH="100vh" bg="#09090b" display="flex" alignItems="center" justifyContent="center" p={4}>
      <Box maxW="md" w="full" bg={cardBg} rounded="lg" shadow="md" p={6} border="1px" borderColor={borderColor}>
        <Text fontSize="2xl" fontWeight="bold" textAlign="center" mb={2} color={textColor}>
          Reset Your Password
        </Text>
        <Text fontSize="sm" textAlign="center" mb={6} color="gray.400">
          Enter a new password for {email}
        </Text>

        {error && (
          <Alert status="error" mb={4} borderRadius="md">
            <AlertIcon />
            {error}
          </Alert>
        )}

        <VStack as="form" onSubmit={handleSubmit} spacing={4}>
          <FormControl>
            <FormLabel color={labelColor} fontSize="sm">New Password</FormLabel>
            <InputGroup>
              <Input
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                bg={inputBg}
                borderColor={inputBorder}
                color="white"
                _focus={{ borderColor: '#3182ce', boxShadow: '0 0 0 1px #3182ce' }}
                placeholder="Enter new password"
              />
              <InputRightElement>
                <IconButton
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  icon={showPassword ? <FaEyeSlash /> : <FaEye />}
                  variant="ghost"
                  color="gray.400"
                  onClick={() => setShowPassword(!showPassword)}
                  size="sm"
                />
              </InputRightElement>
            </InputGroup>

            {/* Password Strength Indicator */}
            {newPassword && (
              <Box mt={3}>
                <HStack justify="space-between" mb={2}>
                  <Text fontSize="sm" color="gray.400">Password Strength:</Text>
                  <Text fontSize="sm" color={strengthColor} fontWeight="medium">
                    {strengthLabel}
                  </Text>
                </HStack>
                <Progress 
                  value={passwordStrength} 
                  colorScheme={passwordStrength < 25 ? 'red' : passwordStrength < 50 ? 'orange' : passwordStrength < 75 ? 'yellow' : 'green'}
                  size="sm"
                  borderRadius="md"
                />
              </Box>
            )}

            {/* Password Requirements */}
            {newPassword && (
              <Box mt={3} p={3} bg="gray.800" borderRadius="md" border="1px" borderColor="gray.700">
                <Text fontSize="sm" color="gray.300" mb={2} fontWeight="medium">
                  Password Requirements:
                </Text>
                <VStack align="start" spacing={1}>
                  {currentPasswordRequirements.map((req, index) => (
                    <HStack key={index} spacing={2}>
                      <Icon 
                        as={req.met ? FaCheck : FaTimes} 
                        color={req.met ? 'green.400' : 'red.400'} 
                        boxSize={3} 
                      />
                      <Text fontSize="xs" color={req.met ? 'green.400' : 'red.400'}>
                        {req.label}
                      </Text>
                    </HStack>
                  ))}
                </VStack>
              </Box>
            )}
          </FormControl>

          <FormControl>
            <FormLabel color={labelColor} fontSize="sm">Confirm New Password</FormLabel>
            <InputGroup>
              <Input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                bg={inputBg}
                borderColor={inputBorder}
                color="white"
                _focus={{ borderColor: '#3182ce', boxShadow: '0 0 0 1px #3182ce' }}
                placeholder="Confirm new password"
              />
              <InputRightElement>
                <IconButton
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  icon={showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                  variant="ghost"
                  color="gray.400"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  size="sm"
                />
              </InputRightElement>
            </InputGroup>
            
            {/* Password match indicator */}
            {confirmPassword && (
              <HStack mt={2} spacing={2}>
                <Icon 
                  as={newPassword === confirmPassword ? FaCheck : FaTimes} 
                  color={newPassword === confirmPassword ? 'green.400' : 'red.400'} 
                  boxSize={3} 
                />
                <Text fontSize="xs" color={newPassword === confirmPassword ? 'green.400' : 'red.400'}>
                  {newPassword === confirmPassword ? 'Passwords match' : 'Passwords do not match'}
                </Text>
              </HStack>
            )}
          </FormControl>

          <Button
            type="submit"
            w="full"
            bg="#3182ce"
            color="white"
            _hover={{ bg: '#2c5aa0' }}
            isLoading={isLoading}
            loadingText="Resetting..."
            isDisabled={!isPasswordValid || newPassword !== confirmPassword}
          >
            Reset Password
          </Button>

          <Button
            variant="ghost"
            w="full"
            color="gray.400"
            _hover={{ bg: 'gray.800' }}
            onClick={() => navigate('/auth')}
          >
            Back to Login
          </Button>
        </VStack>
      </Box>
    </Box>
  )
}
